import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { LeadTaskData } from '@/types/api';

export type LeadTaskStatus = 'open' | 'overdue' | 'due_soon';

export type LeadTaskInput = {
  title: string;
  notes?: string | null;
  due_at: string;
  assigned_to_user_id?: number | null;
};

export type LeadTaskPatch = Partial<LeadTaskInput> & { completed?: boolean };

const TASKS_KEY = ['lead-tasks'] as const;
const OPEN_COUNT_KEY = ['lead-tasks', 'open-count'] as const;

export function useLeadTasks(status: LeadTaskStatus = 'open') {
  return useQuery({
    queryKey: [...TASKS_KEY, status],
    queryFn: async (): Promise<LeadTaskData[]> => {
      const { data } = await api.get<{ data: LeadTaskData[] }>('/v1/lead-tasks', {
        params: { status },
      });
      return data.data;
    },
    refetchOnMount: 'always',
  });
}

export function useOpenLeadTasksCount() {
  return useQuery({
    queryKey: OPEN_COUNT_KEY,
    queryFn: async (): Promise<number> => {
      const { data } = await api.get<{ count: number }>('/v1/lead-tasks/open-count');
      return data.count;
    },
    // Sidebar badge refreshes when the window regains focus.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useCreateLeadTask(leadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LeadTaskInput): Promise<LeadTaskData> => {
      await primeCsrf();
      const { data } = await api.post<LeadTaskData>(`/v1/leads/${leadId}/tasks`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', leadId] });
      qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useUpdateLeadTask(leadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      patch,
    }: {
      taskId: number;
      patch: LeadTaskPatch;
    }): Promise<LeadTaskData> => {
      await primeCsrf();
      const { data } = await api.patch<LeadTaskData>(
        `/v1/leads/${leadId}/tasks/${taskId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', leadId] });
      qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useDeleteLeadTask(leadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/leads/${leadId}/tasks/${taskId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', leadId] });
      qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}
